// scope.ts — the scope editor's form shape and the mappings between the read
// (`UserScopeRead` — what the viewer may read), the form (what the viewer edits)
// and the wire (`ScopeSections` — what the write takes). The sections of ADR-050
// §3: Sites ("All sites", or zones · clusters · extra sites), Depots, Vehicle pools
// and — the pair's third twin, ADR-083 §4 — Teams ("All …", or ids). FRONTEND-ONLY shape, never stricter than the
// backend (CONVENTIONS §Schemas): the server keeps the highest pick again, bounds
// every pick by the giver's own reach, and keeps a person's grants outside it.

import { z } from 'zod';
import type { ScopeSections } from '@ems-app/schemas/users/requests';
import type { UserScopeRead } from '@ems-app/schemas/users/responses';

export const scopeFormShape = z.object({
  sites: z.object({ all: z.boolean(), zone_ids: z.array(z.number().int()), cluster_ids: z.array(z.number().int()), site_ids: z.array(z.string()) }),
  depots: z.object({ all: z.boolean(), ids: z.array(z.string()) }),
  vehicle_pools: z.object({ all: z.boolean(), ids: z.array(z.string()) }),
  teams: z.object({ all: z.boolean(), ids: z.array(z.string()) }),
});
export type ScopeFormValues = z.infer<typeof scopeFormShape>;

/** Nothing granted — the ONE default of a new member (ADR-050 §Consequences). */
export const EMPTY_SCOPE: ScopeFormValues = {
  sites: { all: false, zone_ids: [], cluster_ids: [], site_ids: [] },
  depots: { all: false, ids: [] },
  vehicle_pools: { all: false, ids: [] },
  teams: { all: false, ids: [] },
};

/** What the viewer may switch to "All" — the read's `giver`; a NEW person takes the viewer's own standing. `whole`:
 *  the viewer is project-wide — the last "All" that would make the whole project needs it. */
export type ScopeGiver = UserScopeRead['giver'];

/** The grants outside the viewer's reach, kept untouched by their edit — a count per section (ADR-050 §1). */
export interface ScopeOutside {
  sites: number;
  depots: number;
  vehicle_pools: number;
  teams: number;
}

export const NO_OUTSIDE: ScopeOutside = { sites: 0, depots: 0, vehicle_pools: 0, teams: 0 };

/** The read → the form: the picks inside the viewer's reach are what they edit; the whole project reads as every "All …" on. */
export function scopeFormFromRead(s: UserScopeRead): ScopeFormValues {
  if (s.kind === 'whole') return { sites: { all: true, zone_ids: [], cluster_ids: [], site_ids: [] }, depots: { all: true, ids: [] }, vehicle_pools: { all: true, ids: [] }, teams: { all: true, ids: [] } };
  return {
    sites: { all: s.sites.all, zone_ids: s.sites.zones.map((z) => z.id), cluster_ids: s.sites.clusters.map((c) => c.id), site_ids: s.sites.sites.map((x) => x.id) },
    depots: { all: s.depots.all, ids: s.depots.items.map((d) => d.id) },
    vehicle_pools: { all: s.vehicle_pools?.all ?? false, ids: s.vehicle_pools?.items.map((p) => p.id) ?? [] },
    teams: { all: s.teams?.all ?? false, ids: s.teams?.items.map((t) => t.id) ?? [] },
  };
}

/** The read's outside counts per section (the sites' three kinds summed). */
export function scopeOutsideFromRead(s: UserScopeRead): ScopeOutside {
  return {
    sites: s.sites.outside.zones + s.sites.outside.clusters + s.sites.outside.sites,
    depots: s.depots.outside,
    vehicle_pools: s.vehicle_pools?.outside ?? 0,
    teams: s.teams?.outside ?? 0,
  };
}

/** The form → the wire: the sections as the write takes them (a section whose module is off travels empty — the server ignores it). */
export function scopeSectionsOf(v: ScopeFormValues): ScopeSections {
  return {
    sites: { all: v.sites.all, zone_ids: v.sites.all ? [] : v.sites.zone_ids, cluster_ids: v.sites.all ? [] : v.sites.cluster_ids, site_ids: v.sites.all ? [] : v.sites.site_ids },
    depots: { all: v.depots.all, ids: v.depots.all ? [] : v.depots.ids },
    vehicle_pools: { all: v.vehicle_pools.all, ids: v.vehicle_pools.all ? [] : v.vehicle_pools.ids },
    teams: { all: v.teams.all, ids: v.teams.all ? [] : v.teams.ids },
  };
}

/** Another person's scope, copied: what the giver may grant of it — the read already names only the picks inside the
 *  giver's reach, and an "All …" copies only where the giver may switch it. Answers the values and what was NOT copied. */
export function copyScope(from: UserScopeRead, giver: ScopeGiver): { values: ScopeFormValues; notCopied: number } {
  const base = scopeFormFromRead(from);
  const outside = scopeOutsideFromRead(from);
  let notCopied = outside.sites + outside.depots + outside.vehicle_pools + outside.teams;
  const sitesAll = base.sites.all && giver.sites;
  if (base.sites.all && !giver.sites) notCopied++;
  const depotsAll = base.depots.all && giver.depots;
  if (base.depots.all && !giver.depots) notCopied++;
  const poolsAll = base.vehicle_pools.all && giver.vehicle_pools;
  if (base.vehicle_pools.all && !giver.vehicle_pools) notCopied++;
  const teamsAll = base.teams.all && giver.teams;
  if (base.teams.all && !giver.teams) notCopied++;
  return {
    values: {
      sites: { ...base.sites, all: sitesAll },
      depots: { ...base.depots, all: depotsAll },
      vehicle_pools: { ...base.vehicle_pools, all: poolsAll },
      teams: { ...base.teams, all: teamsAll },
    },
    notCopied,
  };
}

/** Whether the form grants anything at all — the "No scope yet" state of a draft. */
export function scopeIsEmpty(v: ScopeFormValues): boolean {
  return !v.sites.all && v.sites.zone_ids.length + v.sites.cluster_ids.length + v.sites.site_ids.length === 0 && !v.depots.all && v.depots.ids.length === 0 && !v.vehicle_pools.all && v.vehicle_pools.ids.length === 0 && !v.teams.all && v.teams.ids.length === 0;
}
